import type { ReaderOptions, ReadResult } from 'zxing-wasm/reader';

export type ScannerBarcodeFormat = NonNullable<ReaderOptions['formats']>[number];

export const DEFAULT_SCANNER_FORMATS: ScannerBarcodeFormat[] = [
  'PDF417',
  'QRCode',
  'DataMatrix',
  'Code128',
  'Code39',
  'EAN13',
  'EAN8',
  'UPCA',
  'UPCE',
  'ITF',
];

type ZXingReader = typeof import('zxing-wasm/reader');

let readerPromise: Promise<ZXingReader> | undefined;

/**
 * Lazily loads the zxing-wasm reader. The wasm binary is served from /zxing/ (see angular.json assets)
 * instead of the default jsDelivr CDN. Must only be called in the browser.
 */
export function loadZXingReader(): Promise<ZXingReader> {
  if (!readerPromise) {
    readerPromise = import('zxing-wasm/reader')
      .then(async (reader) => {
        await reader.prepareZXingModule({
          overrides: {
            locateFile: (path: string, prefix: string) =>
              path.endsWith('.wasm') ? new URL(`zxing/${path}`, document.baseURI).href : prefix + path,
          },
          fireImmediately: true,
        });
        return reader;
      })
      .catch((error) => {
        // Allow a retry on the next call (e.g. after a network hiccup)
        readerPromise = undefined;
        throw error;
      });
  }
  return readerPromise;
}

export async function readBarcodesFromImageData(
  imageData: ImageData,
  formats: ScannerBarcodeFormat[],
): Promise<ReadResult[]> {
  const reader = await loadZXingReader();
  return reader.readBarcodes(imageData, {
    formats,
    tryHarder: true,
    maxNumberOfSymbols: 1,
  });
}
